import React, { useState, useEffect } from 'react';
import SearchableSelect from '../ui/SearchableSelect';

interface EnquiryFormData {
  source: 'whatsapp' | 'email' | 'agent_portal';
  agency_id: number | undefined;
  customer_name: string;
  customer_reference: string;
  pic_name: string;
  travel_date: string;
  plan_text: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  number_of_adults: number;
  number_of_kids: number;
  number_of_nights: number;
}

interface Agency {
  agency_id: number;
  agency_name: string;
}

interface EnquiryFormProps {
  formData: EnquiryFormData;
  onFormDataChange: (data: EnquiryFormData) => void;
  agencies: Agency[];
  kidsAges: number[];
  onKidsAgesChange: (ages: number[]) => void;
  attachment?: File | null;
  onAttachmentChange?: (file: File | null) => void;
  isEdit?: boolean;
}

const EnquiryForm: React.FC<EnquiryFormProps> = ({
  formData,
  onFormDataChange,
  agencies,
  kidsAges,
  onKidsAgesChange,
  attachment,
  onAttachmentChange,
  isEdit = false
}) => {
  
  const handleInputChange = (field: keyof EnquiryFormData, value: string | number) => {
    try {
      onFormDataChange({
        ...formData,
        [field]: value
      });
    } catch (error) {
      console.error('Error updating form data:', error);
    }
  };

  const agencyOptions = agencies.map(agency => ({
    value: agency.agency_id,
    label: agency.agency_name
  }));

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Agency *</label>
        <SearchableSelect
          options={agencyOptions}
          value={formData.agency_id || ''}
          onChange={(value) => handleInputChange('agency_id', value as number)}
          placeholder="SELECT AGENCY"
        />
      </div>

      {/* Customer Details */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
            <input
              type="text"
              value={formData.customer_name}
              onChange={(e) => handleInputChange('customer_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
            <input
              type="text"
              value={formData.customer_reference}
              onChange={(e) => handleInputChange('customer_reference', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Travel Details */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Travel Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adults *</label>
            <input
              type="number"
              min="1"
              required
              value={formData.number_of_adults}
              onChange={(e) => handleInputChange('number_of_adults', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kids</label>
            <input
              type="number"
              min="0"
              value={formData.number_of_kids}
              onChange={(e) => {
                const count = parseInt(e.target.value) || 0;
                handleInputChange('number_of_kids', count);
                onKidsAgesChange(Array(count).fill(0));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {formData.number_of_kids > 0 && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Kids Ages</label>
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: formData.number_of_kids }).map((_, index) => (
                  <input
                    key={index}
                    type="number"
                    min="0"
                    max="17"
                    placeholder={`Kid ${index + 1}`}
                    value={kidsAges[index] || ''}
                    onChange={(e) => {
                      const newAges = [...kidsAges];
                      newAges[index] = parseInt(e.target.value) || 0;
                      onKidsAgesChange(newAges);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date</label>
            <input
              type="date"
              value={formData.travel_date}
              onChange={(e) => handleInputChange('travel_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nights</label>
            <input
              type="number"
              min="1"
              value={formData.number_of_nights}
              onChange={(e) => handleInputChange('number_of_nights', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Travel Request */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Travel Request *</label>
        <textarea
          required
          rows={4}
          value={formData.plan_text}
          onChange={(e) => handleInputChange('plan_text', e.target.value)}
          placeholder="Describe the travel requirements..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* File Attachment - only show for create */}
      {!isEdit && onAttachmentChange && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Attachment (Optional)</label>
          <input
            type="file"
            onChange={(e) => onAttachmentChange(e.target.files?.[0] || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          />
          {attachment && (
            <p className="text-sm text-gray-600 mt-1">Selected: {attachment.name}</p>
          )}
        </div>
      )}

      {/* Point of Contact */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Point of Contact</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PIC Name *</label>
            <input
              type="text"
              required
              value={formData.pic_name}
              onChange={(e) => handleInputChange('pic_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
            <select
              required
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryForm;